import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';

export default function usePcOverviewLocalesIntl() {
  const { formatMessage } = useIntl(); // 中英切换方法
  const unit = getMonetUnit();
  const explainInfo = useSelector((state: any) => state.dashboard?.explainInfo,shallowEqual);
  // 日期維度常量
  const DATE_DIMENSION = {
    DAY: 'DAY',
    WEEK: 'WEEK',
    MONTH: 'MONTH',
    PCCONSOLEDAY: 'pcConsoleDay'
  };
  // 分解图指标常量
  const BREAKDOWN_METRICS = {
    DAY: {
      AU: formatMessage({ id: 'overview_daily_DAU' }),
      BREAKDOWN_AND_NEWUSERS_TITLE: [formatMessage({ id: 'overview_daily_DAU_Breakdown' }), formatMessage({ id: 'overview_System_of_New_Users' })],
      BREAKDOWN_TOTAL: 'dau_breakdown_total',
      BREAKDOWN: 'dau_breakdown',
      NEW: formatMessage({ id: 'overview_monthly_New_Users' }),
    },
    WEEK: {
      AU: formatMessage({ id: 'overview_weekly_WAU' }),
      BREAKDOWN_AND_NEWUSERS_TITLE: [formatMessage({ id: 'overview_weekly_WAU_Breakdown' }), formatMessage({ id: 'overview_System_of_New_Users' })],
      BREAKDOWN_TOTAL: 'wau_breakdown_total',
      BREAKDOWN: 'wau_breakdown',
      NEW: formatMessage({ id: 'overview_monthly_New_Users' }),
    },
    MONTH: {
      AU: formatMessage({ id: 'overview_monthly_MAU' }),
      BREAKDOWN_AND_NEWUSERS_TITLE: [formatMessage({ id: 'overview_monthly_MAU_Breakdown' }), formatMessage({ id: 'overview_System_of_New_Users' })],
      BREAKDOWN_TOTAL: 'mau_breakdown_total',
      BREAKDOWN: 'mau_breakdown',
      NEW: formatMessage({ id: 'overview_monthly_New_Users' }),
    },
  };

  // 根据接口metrics_name枚举
  interface metirc {
    // 后端接口配置
    metric_name?: "ACU"; // title展示 
    metric_field?: "acu"; // 后端接口字段
    position?: ""; // 位置信息（目前以卡片区域实现3*3）
    component?: "cardItem"; // Card组件类型
    chart?: "lineChart";  // 卡片的图表类型 （可弃用用component区分渲染卡片）
    level?: "parent"; // parent带图表趋势卡片 child数据卡片 （可弃用用component区分渲染卡片）

    // 前端配置
    title: string;  // title展示 （可七彩石配置）
    avgTitle: string; // 长时间标题
    explain?: string; // 指标翻译
    rainbowKey?: string; // 指标翻译key （七彩石配置）
    type: 'int' | 'percent'; // type*(浮窗数据是否加% 枚举:int percent)
    isUserType?: boolean; // 是否保留整数
    icon?: string; // 图标 ['game', 'user-u', 'money', 'pie-chart-icon', 'user-dollar', 'user-session'、'user-pcu'、'user-success','user-plus']
    start?: string; // 标题显示单位start(前) 使用unit区分国内外金钱符号
    end?: string;  // 标题显示单位end(后) 使用unit区分国内外金钱符号
    startUnit?: string; // 数据显示单位startUnit(前)
    endUnit?: string; // 数据显示单位endUnit(后)
    decimal?: number; // 保留位数
  }
  interface metircInfo {
    [key: string]: metirc;
  }

  const METRICS_INFO: metircInfo = {
    units_number: {
      title: formatMessage({ id: 'pc_overview_Units' }),
      avgTitle: formatMessage({ id: 'pc_overview_Units' }),
      explain: getExplainVal(explainInfo, 'Pc Metrics Full Game Units', getLocale()),
      start: '',
      end: '',
      type: 'int',
    },
    new_users: {
      title: formatMessage({ id: 'overview_New' }),
      avgTitle: formatMessage({ id: 'overview_New' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: '',
      end: '',
      type: 'int',
    },
    'd1t1': {
      title: formatMessage({ id: 'pc_overview_D1(T-1)' }),
      avgTitle: formatMessage({ id: 'pc_overview_D1(T-1)' }),
      explain: getExplainVal(explainInfo, 'Pc Metrics D1(T-1)', getLocale()),
      start: '',
      end: '',
      type: 'percent',
      decimal: 2,
    },
    active_users: {
      title: formatMessage({ id: 'overview_daily_DAU' }),
      avgTitle: formatMessage({ id: 'overview_monthly_Avg_DAU' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: 'Avg.',
      end: '',
      type: 'int',
    },
    aot: {
      title: formatMessage({ id: 'overview_AOT(min)' }),
      avgTitle: formatMessage({ id: 'overview_AOT(min)' }),
      start: '',
      end: '',
      type: 'int',
    },
    pcu: {
      title: formatMessage({ id: 'overview_PCU' }),
      avgTitle: formatMessage({ id: 'overview_Avg_PCU' }),
      start: '',
      end: '',
      type: 'int',
    },
    net_revenue: {
      title: formatMessage({ id: 'pc_overview_Revenue' }),
      avgTitle: formatMessage({ id: 'pc_overview_Revenue' }),
      explain: getExplainVal(explainInfo, 'Pc Metrics Net Revenue', getLocale()),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 2,
    },
    mtx_dlc_revenue: {
      title: formatMessage({ id: 'pc_overview_DLC_MTX_Revenue' }),
      avgTitle: formatMessage({ id: 'pc_overview_DLC_MTX_Revenue' }),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 2,
    },
    new_mtx_dlc_revenue: {
      title: formatMessage({ id: 'pc_overview_DLC_MTX_Revenue' }),
      avgTitle: formatMessage({ id: 'pc_overview_DLC_MTX_Revenue' }),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 2,
    },
    mtx_revenue: {
      title: formatMessage({ id: 'pc_overview_MTX_Revenue' }),
      avgTitle: formatMessage({ id: 'pc_overview_MTX_Revenue' }),
      explain: getExplainVal(explainInfo, 'Pc In-Game Revenue', getLocale()),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 2,
    },
    mtx_payer_rate: {
      title: formatMessage({ id: 'pc_overview_MTX_Payer_Rate' }),
      avgTitle: formatMessage({ id: 'pc_overview_MTX_Payer_Rate' }),
      explain: getExplainVal(explainInfo, 'Pc In-Game Payer%', getLocale()),
      start: '',
      end: '',
      type: 'percent',
      decimal: 2,
    },
    mtx_payers: {
      title: formatMessage({ id: 'pc_overview_MTX_Payer' }),
      avgTitle: formatMessage({ id: 'pc_overview_Avg_MTX_Payer' }),
      start: '',
      end: '',
      type: 'int',
    },
    paid_users: {
      title: formatMessage({ id: 'overview_Payer' }),
      avgTitle: formatMessage({ id: 'overview_Avg_Payer' }),
      start: '',
      end: '',
      type: 'int',
    },
    arpu: {
      title: formatMessage({ id: 'overview_ARPU' }),
      avgTitle: formatMessage({ id: 'overview_ARPU' }),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 2,
    },
    mtx_dlc_arpu: {
      title: formatMessage({ id: 'pc_overview_DLC_MTX_ARPU' }),
      avgTitle: formatMessage({ id: 'pc_overview_DLC_MTX_ARPU' }),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 1,
    },
    mtx_arpu: {
      title: formatMessage({ id: 'pc_overview_MTX_ARPU' }),
      avgTitle: formatMessage({ id: 'pc_overview_MTX_ARPU' }),
      explain: getExplainVal(explainInfo,'Pc In-Game ARPU', getLocale()),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 1,
    },
    arppu: {
      title: formatMessage({ id: 'pc_overview_ARPPU' }),
      avgTitle: formatMessage({ id: 'pc_overview_ARPPU' }),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 1,
    },
    mtx_dlc_arppu: {
      title: formatMessage({ id: 'pc_overview_DLC_MTX_ARPPU' }),
      avgTitle: formatMessage({ id: 'pc_overview_DLC_MTX_ARPPU' }),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 1,
    },
    mtx_arppu: {
      title: formatMessage({ id: 'pc_overview_MTX_ARPPU' }),
      avgTitle: formatMessage({ id: 'pc_overview_MTX_ARPPU' }),
      start: '',
      // end: '($)',
      type: 'int',
      decimal: 1,
    },

  };

  // Nightingale
  const NIGHTINGALE_METRICS_INFO: metircInfo = {
    units: {
      title: formatMessage({ id: 'pc_overview_Units' }),
      avgTitle: formatMessage({ id: 'pc_overview_Units' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_units' }),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
      icon: 'game',
    },
    accumulated_units: {
      title: formatMessage({ id: 'nightingale_daily_accumulated_units' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_accumulated_units' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_accumulated_units' }),
      start: '',
      end: '',
      isUserType: true,
      type: 'int',
    },
    refunded_units: {
      title: formatMessage({ id: 'nightingale_daily_refunded_units' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_refunded_units' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_refunded_units' }),
      start: '',
      end: '',
      isUserType: true,
      type: 'int',
    },
    // Accumulated Units
    // Refunded Units
    pay_amount: {
      title: formatMessage({ id: 'pc_overview_Revenue' }),
      avgTitle: formatMessage({ id: 'pc_overview_Revenue' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
      icon: 'money',
    },
    revenue: {
      title: formatMessage({ id: 'pc_overview_Revenue' }),
      avgTitle: formatMessage({ id: 'pc_overview_Revenue' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
      icon: 'money',
    },
    accumulated_revenue: {
      title: formatMessage({ id: 'nightingale_daily_accumulated_revenue' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_accumulated_revenue' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_accumulated_revenue' }),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
    },
    avg_selling_price_asp: {
      title: formatMessage({ id: 'nightingale_daily_avg_selling_price_asp' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_selling_price_asp' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_avg_selling_price_asp' }),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
    },
    // Accumulated Revenue
    // Average Selling Price(ASP)
    active_users: {
      title: formatMessage({ id: 'overview_daily_DAU' }),
      avgTitle: formatMessage({ id: 'overview_monthly_Avg_DAU' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
      icon: 'user-u',
    },
    new_users: {
      title: formatMessage({ id: 'nightingale_daily_new' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_sum_new' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
    },
    reactivated_users: {
      title: formatMessage({ id: 'nightingale_daily_reactivated_users' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_reactivated_users' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_reactivated_users' }),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
    },
    median_online_time: {
      title: formatMessage({ id: 'nightingale_daily_median_online_time' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_median_online_time' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_median_online_time' }),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
      // isUserType: true,
      icon: 'pie-chart-icon',
    },
    aot_hour: {
      title: formatMessage({ id: 'nightingale_daily_aot_hour' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_aot_hour' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_aot_hour' }),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
      // isUserType: true,
    },
    above_2hr_users: {
      title: formatMessage({ id: 'nightingale_daily_above_2hr_users' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_above_2hr_users' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_above_2hr_users' }),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
    },
    median_session_time: {
      title: formatMessage({ id: 'nightingale_daily_median_session_time' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_median_session_time' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_median_session_time' }),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
      icon: 'user-session',
    },
    avg_session_time: {
      title: formatMessage({ id: 'nightingale_daily_avg_session_time' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_session_time' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_avg_session_time' }),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
    },
    avg_session_cnt: {
      title: formatMessage({ id: 'nightingale_daily_avg_session_cnt' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_session_cnt' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_avg_session_cnt' }),
      start: '',
      end: '',
      type: 'int',
      // isUserType: true,
    },
    pcu: {
      title: formatMessage({ id: 'overview_PCU' }),
      avgTitle: formatMessage({ id: 'overview_Avg_PCU' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_pcu' }),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
      icon: 'user-pcu',
    },
    pcu_active_users_rate: {
      title: formatMessage({ id: 'nightingale_daily_pcu_active_users_rate' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_pcu_active_users_rate' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_pcu_active_users_rate' }),
      start: '',
      end: '',
      type: 'percent',
    },
    acu: {
      title: formatMessage({ id: 'nightingale_daily_acu' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_acu' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_acu' }),
      start: '',
      end: '',
      isUserType: true,
      type: 'int',
    },
    stickiness: {
      title: formatMessage({ id: 'nightingale_daily_stickiness' }),
      avgTitle: formatMessage({ id: 'nightingale_avg_daily_stickiness' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_stickiness' }),
      start: '',
      end: '',
      type: 'percent',
      icon: 'user-u',
    },
    active_retention_d2t1: {
      title: formatMessage({ id: 'nightingale_daily_active_retention_d2t1' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_active_retention_d2t1' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_active_retention_d2t1' }),
      start: '',
      end: '',
      type: 'percent',
      icon: 'user-success',
    },
    active_retention_d8t7: {
      title: formatMessage({ id: 'nightingale_daily_active_retention_d8t7' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_active_retention_d8t7' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_active_retention_d8t7' }),
      start: '',
      end: '',
      type: 'percent',
      icon: 'user-success',
    }
  };

  const PC_V2_METRICS_INFO: metircInfo = {
    active_users: {
      title: formatMessage({ id: 'overview_daily_DAU' }),
      avgTitle: formatMessage({ id: 'overview_monthly_Avg_DAU' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
      icon: 'user-u',
    },
    new_users: {
      title: formatMessage({ id: 'overview_New' }),
      avgTitle: formatMessage({ id: 'overview_New' }),
      explain: getExplainVal(explainInfo, '', getLocale()),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
      icon: 'user-plus',
    },
    median_online_time: {
      title: formatMessage({ id: 'nightingale_daily_median_online_time' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_median_online_time' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_median_online_time' }),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
      // isUserType: true,
      icon: 'pie-chart-icon',
    },
    aot_hour: {
      title: formatMessage({ id: 'pc_daily_v2_aot_hour' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_aot_hour' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_aot_hour' }),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
      // isUserType: true,
    },
    above_2hr_users: {
      title: formatMessage({ id: 'pc_daily_v2_above_2hr_users' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_avg_above_2hr_users' }),
      explain: formatMessage({ id: 'pc_overview_daily_v2_key_performance_metrics_explain_above_2hr_users' }),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
    },

    median_session_time: {
      title: formatMessage({ id: 'pc_daily_v2_median_session_time' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_avg_median_session_time' }),
      explain: getExplainVal(explainInfo, 'Pc V2 Metrics Md.Playtime Per Save', getLocale()),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
      icon: 'user-session',
    },
    avg_session_time: {
      title: formatMessage({ id: 'pc_daily_v2_avg_session_time' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_avg_session_time' }),
      explain: getExplainVal(explainInfo, 'Pc V2 Metrics Avg.Playtime Per Save', getLocale()),
      start: '',
      end: '',
      endUnit: 'H',
      type: 'int',
    },
    avg_session_cnt: {
      title: formatMessage({ id: 'pc_daily_v2_avg_session_cnt' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_avg_session_cnt' }),
      explain: getExplainVal(explainInfo, 'Pc V2 Metrics Avg.Save Count', getLocale()),
      start: '',
      end: '',
      type: 'int',
      // isUserType: true,
    },

    pcu: {
      title: formatMessage({ id: 'overview_PCU' }),
      avgTitle: formatMessage({ id: 'overview_Avg_PCU' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_pcu' }),
      start: '',
      end: '',
      type: 'int',
      icon: 'user-pcu',
      isUserType: true,
    },
    pcu_active_users_rate: {
      title: formatMessage({ id: 'nightingale_daily_pcu_active_users_rate' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_avg_pcu_active_users_rate' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_pcu_active_users_rate' }),
      start: '',
      end: '',
      type: 'percent',
    },
    acu: {
      title: formatMessage({ id: 'nightingale_daily_acu' }),
      avgTitle: formatMessage({ id: 'nightingale_daily_acu' }),
      explain: formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_acu' }),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
    },
    d1t1: {
      title: formatMessage({ id: 'pc_daily_v2_d1t1' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_d1t1' }),
      explain: getExplainVal(explainInfo, 'Pc V2 Metrics D1(T-1)', getLocale()),
      start: '',
      end: '',
      type: 'percent',
      icon: 'user-success',
    },
    d2t2: {
      title: formatMessage({ id: 'pc_daily_v2_d2t2' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_d2t2' }),
      explain: getExplainVal(explainInfo, 'Pc V2 Metrics D2(T-2)', getLocale()),
      start: '',
      end: '',
      type: 'percent',
      icon: 'user-success',
    },
    d7t7: {
      title: formatMessage({ id: 'pc_daily_v2_d7t7' }),
      avgTitle: formatMessage({ id: 'pc_daily_v2_d7t7' }),
      explain: getExplainVal(explainInfo, 'Pc V2 Metrics D7(T-7)', getLocale()),
      start: '',
      end: '',
      type: 'percent',
      icon: 'user-success',
    }
  };

  // pc游戏Sales overview指标卡片
  const SALES_OVERVIEW_METRICS_INFO: metircInfo = {
    net_units_sold: {
      title: formatMessage({ id: 'pc_console_sales_overview_net_units_sold' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_net_units_sold' }),
      explain: getExplainVal(explainInfo, 'Pc Net Units',  getLocale()),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
      icon: 'game',
    },
    gross_units_sold: {
      title: formatMessage({ id: 'pc_console_sales_overview_gross_units_sold' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_gross_units_sold' }),
      explain: getExplainVal(explainInfo, 'Pc Gross Units', getLocale()),
      start: '',
      end: '',
      isUserType: true,
      type: 'int',
    },
    third_party_units: {
      title: formatMessage({ id: 'pc_console_sales_overview_third_party_units' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_third_party_units' }),
      explain: getExplainVal(explainInfo, 'Pc Third-party Units', getLocale()),
      start: '',
      end: '',
      isUserType: true,
      type: 'int',
    },
    gross_revenue: {
      title: formatMessage({ id: 'pc_console_sales_overview_gross_revenue' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_gross_revenue' }),
      explain: getExplainVal(explainInfo, 'Pc Gross Revenue', getLocale()),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
      icon: '',
    },
    units_number: {
      title: formatMessage({ id: 'pc_console_sales_overview_units_number' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_units_number' }),
      explain: getExplainVal(explainInfo, 'Pc Sales Overview Metrics Net Full Game Units', getLocale()),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
      icon: 'game',
    },
    accumulated_units: {
      title: formatMessage({ id: 'pc_console_sales_overview_accumulated_units' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_accumulated_units' }),
      explain: getExplainVal(explainInfo, 'Pc Sales Overview Metrics Accumulated Units', getLocale()),
      start: '',
      end: '',
      isUserType: true,
      type: 'int',
    },
    net_revenue: {
      title: formatMessage({ id: 'pc_console_sales_overview_net_revenue' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_net_revenue' }),
      explain: getExplainVal(explainInfo, 'Pc Net Revenue', getLocale()),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
      icon: 'money',
    },
    accumulated_revenue: {
      title: formatMessage({ id: 'pc_console_sales_overview_accumulated_revenue' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_accumulated_revenue' }),
      explain: getExplainVal(explainInfo, 'Pc Sales Overview Metrics Accumulated Revenue', getLocale()),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
      icon: 'money',
    },
    asp: {
      title: formatMessage({ id: 'pc_console_sales_overview_asp' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_asp' }),
      explain: getExplainVal(explainInfo, 'Pc Average Selling Price (ASP)', getLocale()),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
      decimal: 2,
    },
    accumulated_refund_rate: {
      title: formatMessage({ id: 'pc_console_sales_overview_refund_rate' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_refund_rate' }),
      explain: getExplainVal(explainInfo, 'Pc Accumulated Refund Rate', getLocale()),
      start: '',
      end: '',
      type: 'percent',
      icon: 'user-u',
    },
    refund_revenue: {
      title: formatMessage({ id: 'pc_console_sales_overview_refund_revenue' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_refund_revenue' }),
      explain: getExplainVal(explainInfo, 'Pc Refund Revenue', getLocale()),
      start: '',
      end: '',
      startUnit: unit,
      type: 'int',
    },
    refunded_units: {
      title: formatMessage({ id: 'pc_console_sales_overview_refunded_units' }),
      avgTitle: formatMessage({ id: 'pc_console_sales_overview_refunded_units' }),
      explain: getExplainVal(explainInfo, 'Pc Refunded Units', getLocale()),
      start: '',
      end: '',
      type: 'int',
      isUserType: true,
    },
  };

  const METRICS_NAME_DAY = [
    formatMessage({ id: 'overview_daily_DAU' }),
    formatMessage({ id: 'overview_Revenue' }),
    formatMessage({ id: 'overview_New' }),
    formatMessage({ id: 'overview_daily_D2(T-1)' }),
    formatMessage({ id: 'overview_ARPU' }),
    formatMessage({ id: 'overview_Payer%' }),
  ];
  const METRICS_NAME_WEEK = [
    formatMessage({ id: 'overview_weekly_WAU' }),
    formatMessage({ id: 'overview_Revenue' }),
    formatMessage({ id: 'overview_New' }),
    formatMessage({ id: 'overview_weekly_W2(T-1)' }),
    formatMessage({ id: 'overview_ARPU' }),
    formatMessage({ id: 'overview_Payer%' }),
  ];
  const METRICS_NAME_MONTH = [
    formatMessage({ id: 'overview_monthly_MAU' }),
    formatMessage({ id: 'overview_Revenue' }),
    formatMessage({ id: 'overview_New' }),
    formatMessage({ id: 'overview_monthly_Month_M2(T-1)' }),
    formatMessage({ id: 'overview_ARPU' }),
    formatMessage({ id: 'overview_Payer%' }),
  ];

  const EXPLAIN_MAP = {
    dod: 'Day on Day',
    sdlw: 'Same Day Last Week',
    wow: 'Week on Week',
    swlm: 'Same Week Last Month',
    mom: 'Month on Month',
    smly: 'Same Month Last Year',
  };

  const METRICS_EXPLAIN_MAP: any = {
    Day: {
      'units_number': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_units' }),
      'new_users': getExplainVal(explainInfo, '', getLocale()),
      'd1t1': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_d2' }),
      'active_users': getExplainVal(explainInfo, '', getLocale()),
      'aot': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_aot' }),
      'pcu': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_pcu' }),
      'net_revenue': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_revenue' }),
      'mtx_dlc_revenue': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_dlc_mtx_revenue' }),
      'new_mtx_dlc_revenue': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_dlc_mtx_revenue' }),
      'mtx_revenue': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_mtx_revenue' }),
      'mtx_payer_rate': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_mtx_payer_rate' }),
      'mtx_payers': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_mtx_payer' }),
      'paid_users': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_payer' }),
      'arpu': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_arpu' }),
      'mtx_dlc_arpu': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_dlc_mtx_arpu' }),
      'mtx_arpu': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_mtx_arpu' }),
      'arppu': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_arppu' }),
      'mtx_dlc_arppu': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_dlc_mtx_arppu' }),
      'mtx_arppu': formatMessage({ id: 'pc_overview_daily_key_performance_metrics_explain_mtx_arppu' }),
    },
  };


  return {
    EXPLAIN_MAP,
    METRICS_EXPLAIN_MAP,
    BREAKDOWN_METRICS,
    DATE_DIMENSION,
    METRICS_NAME_MONTH,
    METRICS_NAME_WEEK,
    METRICS_NAME_DAY,
    METRICS_INFO,
    NIGHTINGALE_METRICS_INFO,
    PC_V2_METRICS_INFO,
    SALES_OVERVIEW_METRICS_INFO,
  };

}


